/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { MessageBar, MessageBarType } from '@fluentui/react';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CopyProjectModal } from 'src/core/components/CopyProjectModal';
import { getCopyProjectToastData } from 'src/core/components/copyProjectToastData';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { FeatureUnavailableInRegion, ResourceUnSupportedErrorType } from 'src/core/components/FeatureUnavailableInRegion';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { fileService } from 'src/core/services/api/fileService';
import {
    logButtonClick,
    logButtonSuccessfulClick,
    logMultiLingualInProjectCreation,
    logProjectClicked,
    logSelectLanguageInProjectCreation
} from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { isCustomTextResource } from 'src/core/utils/csAccountUtils';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { CustomTextProjectsCommandBar } from 'src/customText/appShell/projectsListPage/components/CustomTextProjectsCommandBar';
import { CustomTextProjectsTable } from 'src/customText/appShell/projectsListPage/components/CustomTextProjectsTable';
import { CustomTextProjectKind, CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';
import { getCustomTextProjectImportDataFromFileContent } from 'src/customText/modules/handlers/projects/projectMappers';
import { ProjectCreationModal, ProjectInfoDto } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { SignedOutEmptyCustomization } from 'src/languageStudio/appShell/SignedOutEmptyCustomization';
import { EmptyProjectsListView } from 'src/modules/projects/EmptyProjectsListView';

export type modelType = 'classification' | 'extraction';

const messages = defineMessages({
    importModalTitle: { id: 'customText.customTextProjectsListPage.importModalTitle', defaultMessage: 'Import project' },
    exportRunningToast: { id: 'customText.customTextProjectsListPage.exportRunningToast', defaultMessage: 'Exporting project ...' },
    pageTitle: {
        id: 'customText.customTextProjectsListPage.extractionPageTitle',
        defaultMessage: 'Select the project you want to work with'
    },
    extractionSubtitle: {
        id: 'customText.customTextProjectsListPage.extractionSubtitle',
        defaultMessage:
            'A custom named entity recognition project contains both the documents that you annotate with entity labels as well as the models that are trained from that data.'
    },
    classificationSubtitle: {
        id: 'customText.customTextProjectsListPage.classificationSubtitle',
        defaultMessage:
            'A custom text classification project contains both the documents that you categorize into classes as well as the models that are trained from that data.'
    },
    deletingProjectTitle: {
        id: 'customText.customTextProjectsListPage.deletingProjectTitle',
        defaultMessage: 'Delete selected project(s)'
    },
    deletingProjectBody: {
        id: 'customText.customTextProjectsListPage.deletingProjectBody',
        defaultMessage: 'Are you sure you want to delete the selected project(s)?'
    },
    failedDeletingProjects: {
        id: 'customText.customTextProjectsListPage.failedDeletingProjects',
        defaultMessage: '{failedCount} projects failed.'
    },
    noResourceSelected: {
        id: 'customText.customTextProjectsListPage.noResourceSelected',
        defaultMessage: 'Please select subscription and azure resource to list your projects.'
    },
    exportCompletedToast: {
        id: 'customText.customTextProjectsListPage.exportCompletedToast',
        defaultMessage: 'Export completed successfully'
    },
    projectNameAlreadyExistsWarningMessage: {
        id: 'customText.customTextProjectsListPage.projectNameAlreadyExistsWarningMessage',
        defaultMessage: 'Project {projectName} already exists, Importing this file will overwrite the existing project.'
    },
    accessDeniedByRole: {
        id: 'customText.customTextProjectsListPage.accessDeniedByRole',
        defaultMessage:
            'The user must have "Storage Blob Data Contributor" role in the Azure Blob storage account to run Custom Text Classification'
    },
    accessDeniedByCORS: {
        id: 'customText.customTextProjectsListPage.accessDeniedByCORS',
        defaultMessage: 'Make sure your CORS is configured correctly on your storage resource'
    }
});

const { styleDiv } = getStylistV2('CustomTextProjectsListPage');
const ProjectsTableWindow = styleDiv('ProjectsTableWindow', { height: '100%', overflowY: 'auto' });

const defaultModalsState = {
    isCopyProjectModalHidden: true,
    isCreateProjectModalHidden: true,
    isDeleteProjectModalHidden: true,
    isImportProjectModalHidden: true
};

export const CustomTextProjectsListPage = observer((props: { areExtractionProjects: boolean }) => {
    const { areExtractionProjects } = props;

    const history = useHistory();
    const { serviceName } = getPortalFromUrl();
    const { localize } = useLocalization();
    const { isAccessibleMode } = useZoomLevel();
    const [query, setQuery] = React.useState('');
    const { dispatcher, rootStore } = useGlobalState();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const { selection, selectedItems } = useSelection<CustomTextProjectStore>();
    const [sortKey, setSortKey] = React.useState<SortKeyType<CustomTextProjectStore>>({ key: 'modifiedDate', order: 'desc' });

    const { isSignedIn } = rootStore.authStore;
    const { customTextProjectStoreHouse, customTextCultureStorehouse, accessError } = rootStore;
    const { csAccountStore } = rootStore.azureStore.session;
    const featureName = areExtractionProjects ? 'Custom named entity recognition' : 'Custom text classification';
    const linkForRegionalAvailability = areExtractionProjects ? 'https://aka.ms/ct-regions' : 'https://aka.ms/ctc-regions';

    const [{ projectNameToImport, importProjectWarningMessage }, setImportInfo] = React.useState({
        projectNameToImport: '',
        importProjectWarningMessage: ''
    });

    const openProject = (projectId: string, projectType?: CustomTextProjectKind): void => {
        logProjectClicked(serviceName, 'ProjectList', projectId);
        history.push(
            routeTable.customText.projectPage(
                projectId,
                projectType ?? (areExtractionProjects ? 'CustomEntityRecognition' : 'CustomSingleLabelClassification')
            )
        );
    };

    const defaultOperationsTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
        }
    });

    const importTracker = useProgressTracker({
        onComplete: (projectName: string) => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
            openProject(projectName);
        }
    });

    const filteredProjects = orderBy(
        substringSearchStrategy(
            customTextProjectStoreHouse.items.filter(p =>
                areExtractionProjects ? p.projectKind === 'CustomEntityRecognition' : p.projectKind !== 'CustomEntityRecognition'
            ),
            query,
            'name'
        ),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredProjects, 10);

    React.useEffect(() => {
        if (csAccountStore?.isCustomTextCompatibleResource && history.location.state && (history.location.state as boolean)) {
            setModalsState({ isCreateProjectModalHidden: false });
        }
    }, []);

    React.useEffect(() => {
        if (
            rootStore.serviceKey.value &&
            rootStore.azureStore.session.csAccountStore?.kind === 'TextAnalytics' &&
            LuisConstants.CustomText.PrivatePreviewAllowedRegions.some(r => r === csAccountStore.region)
        ) {
            dispatcher.dispatch('getCustomTextProjects', { toastData: {} });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id, rootStore.azureStore.session.csAccountStore?.kind]);

    const handleColumnClick = (c: IColumn) => {
        setSortKey({ key: c.key as keyof CustomTextProjectStore, order: c.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        selection.setAllSelected(false);
    };

    const handleDeleteProject = () => {
        dispatcher.dispatch('deleteCustomTextProject', {
            toastData: {},
            tracker: defaultOperationsTracker,
            projectNames: selectedItems.map(item => item.name),
            generateMultiFailureMessage: (failedCount: number) => localize(messages.failedDeletingProjects, { failedCount })
        });

        selectedItems.forEach(item => logButtonSuccessfulClick(serviceName, 'ProjectList', 'Delete', null, item.name));
    };

    const getCultureName = (code: string) => customTextCultureStorehouse.items.find(item => item.code === code)?.name ?? '';

    const handleProjectCreation = (dto: ProjectInfoDto) => {
        logSelectLanguageInProjectCreation(serviceName, getCultureName(dto.defaultLanguage), dto.projectName);

        if (dto.multiLingual) {
            logMultiLingualInProjectCreation(serviceName, dto.projectName);
        }

        setModalsState({ isCreateProjectModalHidden: true });
        openProject(dto.projectName, dto.projectKind);
    };

    const handleExportProject = () => {
        dispatcher.dispatch('exportCustomTextProject', {
            projectName: selectedItems[0].name,
            tracker: defaultOperationsTracker,
            toastData: {
                running: { title: localize(messages.exportRunningToast) },
                completed: { title: localize(messages.exportCompletedToast) }
            }
        });
        logButtonClick(serviceName, 'ProjectList', 'Export', null, selectedItems[0].name);
    };

    const handleImportProject = (file: File) => {
        dispatcher.dispatch('importCustomTextProject', {
            file,
            newProjectName: projectNameToImport,
            isExtractionProject: areExtractionProjects,
            tracker: importTracker,
            toastData: {}
        });
        logButtonSuccessfulClick(serviceName, 'ProjectList', 'Import', null, projectNameToImport);
    };

    const handleCopyProject = (allowOverwrite: boolean, resourceSelected: AzureCsAccountStore, selectedSubscriptionId: string) => {
        dispatcher.dispatch('copyCustomTextProject', {
            allowOverwrite,
            targetResource: resourceSelected,
            tracker: defaultOperationsTracker,
            projectName: selectedItems[0].name,
            projectKind: selectedItems[0].projectKind,
            storageInputContainerName: selectedItems[0].containerName,
            toastData: getCopyProjectToastData({
                localize,
                dispatcher,
                selectedSubscriptionId,
                resourceName: resourceSelected.name,
                projectName: selectedItems?.[0].name,
                selectedResourceId: resourceSelected.id
            })
        });

        setModalsState({ isCopyProjectModalHidden: true });
    };

    const validateProjectNameWhileImport = async (file: File, projectName: string) => {
        let name: string = projectName;

        if (!name && file) {
            const fileContent = await fileService.readFromDisk(file);
            name = getCustomTextProjectImportDataFromFileContent(fileContent).projectName;
        }

        if (customTextProjectStoreHouse.items.some(p => p.name === name)) {
            setImportInfo({
                projectNameToImport: name,
                importProjectWarningMessage: localize(messages.projectNameAlreadyExistsWarningMessage, { projectName: name })
            });
        } else {
            setImportInfo({ projectNameToImport: name, importProjectWarningMessage: '' });
        }
    };

    const commandBar = (
        <CustomTextProjectsCommandBar
            query={query}
            onQueryChange={setQuery}
            selectedItems={selectedItems}
            onExportProject={handleExportProject}
            areExtractionProjects={areExtractionProjects}
            isCreateProjectModalHidden={modalsState.isCreateProjectModalHidden}
            onCopyProject={() => {
                setModalsState({ isCopyProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Copy');
            }}
            onImportProject={() => {
                setModalsState({ isImportProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Import');
            }}
            onCreateProject={() => {
                setModalsState({ isCreateProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'CreateProject');
            }}
            onDeleteProjects={() => {
                setModalsState({ isDeleteProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Delete');
            }}
        />
    );

    if (!isSignedIn) {
        return (
            <Page size="container-fluid" pageName="ProjectsListPage" title={localize(messages.pageTitle)}>
                <EditableTitle
                    title={localize(messages.pageTitle)}
                    subtitle={localize(areExtractionProjects ? messages.extractionSubtitle : messages.classificationSubtitle)}
                />

                {commandBar}

                <SignedOutEmptyCustomization
                    service={areExtractionProjects ? 'customExtraction' : 'customClassification'}
                    serviceDocumentationLink={
                        areExtractionProjects ? 'https://aka.ms/customtextextraction' : 'https://aka.ms/language/customclassification'
                    }
                />
            </Page>
        );
    }

    const errorType: ResourceUnSupportedErrorType = !csAccountStore?.isTextAnalyticsResourceKind
        ? 'unsupportedResource'
        : 'unsupportedRegion';

    const errorMessage = ({ show, statusCode }: { show: boolean; statusCode?: number }) => {
        if (show) {
            let errMessage = '';
            if (typeof statusCode === 'undefined') {
                errMessage = localize(messages.accessDeniedByCORS);
            }
            if (statusCode === 403) {
                errMessage = localize(messages.accessDeniedByRole);
            }
            return (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    onDismiss={() => rootStore.setAccessError(false, undefined)}
                    dismissButtonAriaLabel="Close">
                    {errMessage}
                </MessageBar>
            );
        }
    };
    return (
        <Page size="container-fluid" pageName="ProjectsListPage" title={localize(messages.pageTitle)}>
            <EditableTitle
                title={localize(messages.pageTitle)}
                subtitle={localize(areExtractionProjects ? messages.extractionSubtitle : messages.classificationSubtitle)}
            />
            {errorMessage(accessError)}

            {csAccountStore?.isCustomTextCompatibleResource ? (
                <>
                    {commandBar}

                    {customTextProjectStoreHouse.tracker.isRunning || pageItems.length ? (
                        <ProjectsTableWindow data-is-scrollable={!isAccessibleMode}>
                            <CustomTextProjectsTable
                                sortKey={sortKey}
                                items={pageItems}
                                selection={selection}
                                onProjectClick={openProject}
                                areItemsFiltered={Boolean(query)}
                                overallItemsCount={filteredProjects.length}
                                onSortColumn={handleColumnClick}
                                areClassificationProjects={!areExtractionProjects}
                                isLoading={customTextProjectStoreHouse.tracker.isRunning}
                                announcedMessage={localize(globalMessages.invalidRegion, { featureName })}
                            />
                            <TablePagination
                                pageCount={pageCount}
                                currentPage={currentPage}
                                onPageChange={p => handlePageChange(p.selected)}
                            />
                        </ProjectsTableWindow>
                    ) : (
                        <EmptyProjectsListView message={!csAccountStore ? localize(messages.noResourceSelected) : undefined} />
                    )}
                </>
            ) : (
                <FeatureUnavailableInRegion featureName={featureName} docsLink={linkForRegionalAvailability} errorType={errorType} />
            )}

            <ProjectCreationModal
                isExtractionProject={areExtractionProjects}
                hidden={modalsState.isCreateProjectModalHidden}
                onSubmit={handleProjectCreation}
                onDismiss={() => setModalsState({ isCreateProjectModalHidden: true })}
            />

            <CopyProjectModal
                hidden={modalsState.isCopyProjectModalHidden}
                onDismiss={() => setModalsState({ isCopyProjectModalHidden: true })}
                serviceTarget="TextAnalytics"
                strategies={{
                    resource: isCustomTextResource
                }}
                onCopyProjectClicked={handleCopyProject}
                isSupportBYOS
            />

            <DeletePromptModal
                onDelete={handleDeleteProject}
                disabled={defaultOperationsTracker.isRunning}
                hidden={modalsState.isDeleteProjectModalHidden}
                dialogContentProps={{ title: localize(messages.deletingProjectTitle) }}
                onDismiss={() => setModalsState({ isDeleteProjectModalHidden: true })}>
                <LuisText>{localize(messages.deletingProjectBody)}</LuisText>
            </DeletePromptModal>

            <ImportModal
                fileType="json"
                disabled={importTracker.isRunning}
                warningMessage={importProjectWarningMessage}
                hidden={modalsState.isImportProjectModalHidden}
                validateName={validateProjectNameWhileImport}
                onSubmit={({ file }) => handleImportProject(file)}
                dialogContentProps={{ title: localize(messages.importModalTitle) }}
                onDismiss={() => setModalsState({ isImportProjectModalHidden: true })}
            />
        </Page>
    );
});
